/**
 * Akkreditering (PEOPLE.md, fane 3).
 *
 * ---------------------------------------------------------------------------------------------
 * A ROW HERE IS NOT A ROW IN A TABLE. «Listen fylles automatisk fra tre kilder», so the list is a
 * union over approved volunteers, travel parties and own staff, plus the manual entries that exist
 * only in `accreditations`. `id` is therefore NULLABLE: somebody the sources list but nobody has
 * touched yet has no row of their own, and the page has to be able to say so — that is exactly the
 * person whose card has not been handed out.
 *
 * The write routes take a PERSON and a year rather than an id, for the same reason: the desk hands
 * a card to a person, and whether a row already exists for them is the API's problem.
 */
import { z } from 'zod';

import { accreditationColourSchema, MAX_PASS_DAYS } from './accreditation.js';
import { categoryListParamSchema, participationCategorySchema } from './participation-category.js';

/**
 * Which automatic source a name came from, or that it was added by hand. `participant` is a
 * participation with a level and no other role (219.1).
 */
export const ACCREDITATION_SOURCES = [
  'volunteer',
  'travel',
  'crew',
  'guest',
  'manual',
  'participant',
  /** 239.19: står på en vakt i årgangen (medlem eller kontakt), uten annen rolle på lista. */
  'shift',
] as const;
export const accreditationSourceSchema = z.enum(ACCREDITATION_SOURCES);
export type AccreditationSource = z.infer<typeof accreditationSourceSchema>;

/**
 * Where the level on the wristband came from (201.16, 204, 215.21), in the order they win:
 *
 *   * `explicit` — set in Akkreditering («Satt for hånd»).
 *   * `cleared` — «Ingen akkreditering», set in Akkreditering. Beats a shift; the level is null.
 *   * `source` — given by one of the person's roles: staff, participation, travel party or volunteer.
 *   * `shift` — given by a post on one of their shifts this year («Fra vakt: Bar»).
 */
export const ACCREDITATION_LEVEL_SOURCES = ['explicit', 'cleared', 'source', 'shift'] as const;
export const accreditationLevelSourceSchema = z.enum(ACCREDITATION_LEVEL_SOURCES);
export type AccreditationLevelSource = z.infer<typeof accreditationLevelSourceSchema>;

/**
 * NIVÅET PÅ ARMBÅNDET, slik de andre skjermene viser det (215.21).
 *
 * Atle: «Akkrediteringens løste nivå (det på armbåndet) er fasit.» Frivillige, deltakere og
 * reisefølget bærer fortsatt sin egen kolonne — det er den skjermens eget valg, og det den skriver —
 * men det de VISER er dette, så ingen skjerm sier Backstage mens armbåndet sier Publikum.
 */
export const resolvedAccreditationLevelSchema = z
  .object({
    /** Null when the wristband has no level, including when it is `cleared`. */
    levelId: z.uuid().nullable(),
    name: z.string().nullable(),
    colour: accreditationColourSchema.nullable(),
    /** Null when nothing gives a level at all. */
    source: accreditationLevelSourceSchema.nullable(),
    /** The post that gave it, when `source` is `shift`. */
    postName: z.string().nullable(),
  })
  .meta({ id: 'ResolvedAccreditationLevel' });
export type ResolvedAccreditationLevel = z.infer<typeof resolvedAccreditationLevelSchema>;

/**
 * Hva en person er akkreditert SOM — organisasjonens egne ord (sak 57, avklart 2. september).
 *
 * ---------------------------------------------------------------------------------------------
 * KILDEN ER IKKE TYPEN, OG DET VAR HELE FEILEN.
 *
 * Kolonnen het «Kilde» og viste `volunteer | travel | crew | manual` — altså hvor raden kom fra.
 * Atle: «Ene vil være om det er en artist, stab, ledelse, frivillig etc.» Det er et annet spørsmål,
 * og «Reisefølge» svarer ikke på det: reisefølget rommer både artisten og ledelsen hens, og
 * `performance_people.party` har visst forskjellen hele tiden.
 *
 * ---------------------------------------------------------------------------------------------
 * `binding` KNYTTER ET NAVN TIL EN UTLEDNING.
 *
 * Fire av dem er automatiske — `crew`, `artist`, `management`, `volunteer` — og kan bare finnes
 * én gang hver: to typer som begge påsto å være reisefølget ville gitt samme person to kort.
 * `manual` er den femte og den eneste som gjentas, fordi presse, sponsor og leverandør alle er
 * «noen disken la inn selv» og skal stå som tre valg i én liste.
 *
 * Bindingen flyttes ikke etterpå. Det er Atles svar på spørsmål B: en tour manager har «Tour
 * manager» i tittelen sin på programposten, og kolonne to er artistnavnet hen representerer —
 * disken retter ikke typen, programposten er stedet.
 */
export const ACCREDITATION_BINDINGS = [
  'crew',
  'artist',
  'management',
  'volunteer',
  /* «Type er "Gjest"» (Atle, 2. september) — gjestelistene, som er den femte kilden. */
  'guest',
  'manual',
] as const;
export const accreditationBindingSchema = z.enum(ACCREDITATION_BINDINGS);
export type AccreditationBinding = z.infer<typeof accreditationBindingSchema>;

/** How many types one organisation may keep. A desk's vocabulary, not a taxonomy. */
export const MAX_ACCREDITATION_TYPES = 30;

export const accreditationTypeDtoSchema = z
  .object({
    id: z.uuid(),
    /**
     * HVILKEN ÅRGANG TYPEN HØRER TIL (K-1). `null` er husets eget sett.
     *
     * Feltet står i svaret og ikke bare i spørringen fordi klienten skriver med det: en
     * omdøping som sendes uten å vite hvilket år raden tilhører kan ikke sjekke sitt eget
     * cache-nøkkelrom, og to årganger har rader med samme `key`.
     */
    editionId: z.uuid().nullable(),
    key: z.string(),
    name: z.string(),
    binding: accreditationBindingSchema,
    sortOrder: z.number().int(),
  })
  .meta({ id: 'AccreditationType' });
export type AccreditationTypeDto = z.infer<typeof accreditationTypeDtoSchema>;

/**
 * HVILKET SETT — PÅKREVD, og det er rettelsen av en målt feil.
 *
 * Probe 19. september, `/settings/akkrediteringstyper` i God Nok: listen viste SEKSTEN rader der
 * åtte er riktig — «Artist, Artist, Ledelse, Ledelse, Stab, Stab …». Migrering 0257 ga hver årgang
 * sitt eget sett, mens `GET /accreditation-types` fortsatt spurte «alt huset eier» og fikk begge
 * årgangenes kopier. Ingen 500, ingen advarsel, bare hvert ord to ganger.
 *
 * `listDepartmentsQuerySchema` lar `editionId` være valgfri og lar fravær bety malen. Den formen
 * er feil her: det finnes ikke lenger noen mal, og et kall som glemmer feltet ville fått et svar
 * som ser riktig ut for en organisasjon med én årgang og er dobbelt for en med to. Påkrevd er den
 * eneste varianten som ikke kan svare feil i stillhet.
 */
export const listAccreditationTypesQuerySchema = z
  .object({ editionId: z.uuid() })
  .meta({ id: 'ListAccreditationTypesQuery' });
export type ListAccreditationTypesQuery = z.infer<typeof listAccreditationTypesQuerySchema>;

export const accreditationTypeListResponseSchema = z
  .object({ items: z.array(accreditationTypeDtoSchema) })
  .meta({ id: 'AccreditationTypeListResponse' });
export type AccreditationTypeListResponse = z.infer<typeof accreditationTypeListResponseSchema>;

const typeName = z.string().trim().min(1).max(60);

export const createAccreditationTypeRequestSchema = z
  .object({
    /** Påkrevd av samme grunn som på lista, og med samme følge: null finnes ikke som valg. */
    editionId: z.uuid(),
    name: typeName,
    sortOrder: z.number().int().min(0).max(10_000).optional(),
  })
  .meta({ id: 'CreateAccreditationTypeRequest' });
export type CreateAccreditationTypeRequest = z.infer<typeof createAccreditationTypeRequestSchema>;

/** The name and the order move; the key and the binding do not — see the header. */
export const updateAccreditationTypeRequestSchema = z
  .object({
    name: typeName.optional(),
    sortOrder: z.number().int().min(0).max(10_000).optional(),
  })
  .meta({ id: 'UpdateAccreditationTypeRequest' });
export type UpdateAccreditationTypeRequest = z.infer<typeof updateAccreditationTypeRequestSchema>;

/**
 * Hvem et kort er FOR, som én streng.
 *
 * ---------------------------------------------------------------------------------------------
 * `person:<uuid>` eller `guest:<uuid>`, og grunnen er at lista har fem kilder hvorav én ikke har
 * noen person. En gjesteliste er navn en tour manager sender på e-post; `performance_guests` er
 * med vilje «et navn og et antall uten en kontakt bak». Nøkkelen på raden måtte derfor bli bredere
 * enn en person-id.
 *
 * ÉN STRENG FRAMFOR ET PAR, fordi den brukes som Reacts `key`, som medlem i et `Set` av markerte
 * rader og som elementet i massehandlingens liste. Et objekt på alle tre stedene ville krevd en
 * sammenligningsfunksjon per sted, og den fjerde ville blitt glemt.
 */
export const accreditationSubjectSchema = z
  .string()
  .regex(/^(person|guest):[0-9a-f-]{36}$/, 'A subject is person:<uuid> or guest:<uuid>.');

/** The ceiling `performance_guests.seats` already carries — one row cannot hand out more. */
export const MAX_SEATS_PER_ROW = 100;

export const accreditationDtoSchema = z
  .object({
    /** Null when nobody has touched this subject here — see the header. */
    id: z.uuid().nullable(),
    /** `person:<uuid>` or `guest:<uuid>`. The row's identity, and what a write is keyed on. */
    subject: accreditationSubjectSchema,
    /** Null for a guest, who has no person and never will — see `accreditationSubjectSchema`. */
    personId: z.uuid().nullable(),
    contactId: z.uuid().nullable(),
    displayName: z.string().nullable(),
    email: z.string().nullable(),
    /**
     * HVOR MANGE KORT DENNE RADEN ER.
     *
     * 1 for a person. For a guest it is `performance_guests.seats`: «Kari Nordmann +2» is one line
     * on the list and three wristbands over the desk. Atle, 2. september: «når man akkrediterer
     * noen inne i artisten der de har flere plasser (gjest med 3 plasser), så må man akkreditere ut
     * alle på en gang» — which is true by construction here, because the row is the guest rather
     * than the seat and there is no state in which two of three are handed out.
     */
    seats: z.number().int().positive(),

    /** Where the name came from. Still here: it is what `rank` folds two sources by. */
    source: accreditationSourceSchema,
    /**
     * What they are here AS, resolved from the organisation's own list — the first of Atle's two
     * columns. Null only for a manual entry nobody has typed a type onto yet, which the desk draws
     * as a gap rather than as a blank.
     */
    accreditationTypeId: z.uuid().nullable(),
    accreditationTypeName: z.string().nullable(),
    /**
     * «Rolle, avdeling eller act» — one column, filled by whichever the source has: the department
     * for staff, the act for a travel party, the free-text role for a press pass.
     */
    context: z.string().nullable(),
    roleLabel: z.string().nullable(),

    accreditationLevelId: z.uuid().nullable(),
    /** Null when the row has no level. */
    levelSource: accreditationLevelSourceSchema.nullable(),
    /** The post that gave the level, when `levelSource` is `shift`; otherwise null. */
    levelFromPostName: z.string().nullable(),
    accreditationLevelName: z.string().nullable(),
    /**
     * «Ha denne fargen synlig over hele rekke med akkrediteringsnivå» (Atle, 2. september).
     *
     * Carried on the ROW rather than looked up from the level list in the client, so the two pages
     * that draw a level cannot disagree — which is the thing he was pointing at: «Nå er det
     * forskjellig mellom sidene.»
     */
    accreditationLevelColour: accreditationColourSchema.nullable(),
    /** «Hvilke soner nivået gir tilgang til», carried so the row can be read on its own. */
    zones: z.array(z.string()),

    /**
     * HVA DE FAAR UTLEVERT — den tredje aksen (QA13 runde 2, #25a).
     *
     * Nivaaet sier hvor de kommer inn, typen hva de er her som, og DENNE hva som ligger paa
     * disken. Null er «ikke bestemt ennaa», som lista tegner som en mangel og ikke som en blank.
     */
    accreditationPassId: z.uuid().nullable(),
    accreditationPassName: z.string().nullable(),
    /** Dagene, naar passtypen krever dem. Tom for et festivalpass. */
    passDays: z.array(z.iso.date()),

    /**
     * B1: PERSONEN I ÅRGANGEN, SOM PÅ PERSONER. Akkreditering er deltakelsen + gjestelistene, og
     * kolonnene heter det samme på begge faner: Kategori, Rolle, Avdeling, Nivå, Pass. Null for en
     * gjest (ingen deltakelse) — gjesten har kategorien `guest` og acten sin.
     */
    participationId: z.uuid().nullable(),
    category: participationCategorySchema,
    /** Rollen fra raden (`role_title`), og rollene fra Innstillinger for seg. Aldri vaktposten. */
    roleTitle: z.string().nullable(),
    roleNames: z.array(z.string()),
    departmentNames: z.array(z.string()),
    acts: z.array(z.string()),

    /** When the FIRST pass on this row went over the desk. Null while none have. */
    handedOutAt: z.iso.datetime().nullable(),
    handedOutByName: z.string().nullable(),
    /**
     * HVOR MANGE AV `seats` SOM ER LEVERT UT — 0, alle, eller noe imellom.
     *
     * Atle, 2. september: «Ingrid Solheim har 2 plasser, men jeg må levere de ut samtidig. Jeg
     * trenger funksjonalitet for å kun levere ut 1 om gangen.» Mellomtilstanden er ekte, og den
     * er der disken står når den ene følgesvennen har hentet og den andre ikke har kommet.
     */
    handedOutCount: z.number().int().nonnegative(),
    note: z.string().nullable(),
  })
  .meta({ id: 'Accreditation' });
export type AccreditationDto = z.infer<typeof accreditationDtoSchema>;

/** One level, with how many people carry it — «antall personer på nivået, og fargen på kortet». */
export const accreditationZoneSummarySchema = z
  .object({
    levelId: z.uuid(),
    name: z.string(),
    colour: accreditationColourSchema.nullable(),
    zones: z.array(z.string()),
    people: z.number().int().nonnegative(),
  })
  .meta({ id: 'AccreditationZoneSummary' });
export type AccreditationZoneSummary = z.infer<typeof accreditationZoneSummarySchema>;

export const accreditationListResponseSchema = z
  .object({
    items: z.array(accreditationDtoSchema),
    /**
     * «Antall akkrediteringer, utlevert med prosent, ikke hentet, og hvor mange mangler nivå.»
     *
     * COUNTED IN CARDS, NOT IN ROWS. A guest with three seats is one line and three cards, and the
     * number the desk plans around — how many wristbands leave the table — is the second one.
     */
    total: z.number().int().nonnegative(),
    handedOut: z.number().int().nonnegative(),
    missingLevel: z.number().int().nonnegative(),
    levels: z.array(accreditationZoneSummarySchema),
  })
  .meta({ id: 'AccreditationListResponse' });
export type AccreditationListResponse = z.infer<typeof accreditationListResponseSchema>;

/**
 * «Alle tabeller på siden skal ha sortering på kolonnene» (PEOPLE.md).
 *
 * ABSENT MEANS THE PAGE'S OWN ORDER, which is not alphabetical and is not an accident: the people
 * with no level sort first, «siden det er den mangelen som stopper folk i inngangen». A reader who
 * clicks a column has asked for something else and gets it; a reader who has not clicked anything
 * gets the list opened on the problem.
 */
/** `category` og `role` er B1-kolonnene; `context` og `source` står for gamle lenker og eksporter. */
export const ACCREDITATION_SORT_KEYS = [
  'level',
  'name',
  'context',
  'source',
  'handedOut',
  'category',
  'role',
] as const;
export const accreditationSortKeySchema = z.enum(ACCREDITATION_SORT_KEYS);
export type AccreditationSortKey = z.infer<typeof accreditationSortKeySchema>;

export const listAccreditationsQuerySchema = z
  .object({
    editionId: z.uuid(),
    /** «Alle typer» filters on the organisation's own list now, not on where the row came from. */
    accreditationTypeId: z.uuid().optional(),
    /** «Filtre for ikke hentet, utlevert og uten nivå.» */
    state: z.enum(['handed_out', 'not_handed_out', 'no_level']).optional(),
    category: participationCategorySchema.optional(),
    q: z.string().trim().min(1).max(120).optional(),
    /** Kategoriene søketeksten treffer på brukerens språk — som på Personer (punkt 53). */
    qCategories: categoryListParamSchema.optional(),
    sort: accreditationSortKeySchema.optional(),
    direction: z.enum(['asc', 'desc']).optional(),
  })
  .meta({ id: 'ListAccreditationsQuery' });
export type ListAccreditationsQuery = z.infer<typeof listAccreditationsQuerySchema>;

/**
 * «Presse, sponsorer og leverandører» — somebody who exists only here.
 *
 * `personId` rather than a name: an accreditation is a badge for a PERSON, and the register is
 * where people are made. A press pass for somebody with no contact card is a contact card waiting
 * to be written, not a name typed onto a list.
 */
export const createAccreditationRequestSchema = z
  .object({
    editionId: z.uuid(),
    personId: z.uuid(),
    accreditationLevelId: z.uuid().nullish(),
    /** Which of the organisation's types. Only a `manual` one can be chosen here. */
    accreditationTypeId: z.uuid().nullish(),
    roleLabel: z.string().trim().min(1).max(120),
    company: z.string().trim().max(120).nullish(),
    note: z.string().trim().max(2000).nullish(),
  })
  .meta({ id: 'CreateAccreditationRequest' });
export type CreateAccreditationRequest = z.infer<typeof createAccreditationRequestSchema>;

/**
 * Setting a level, handing a card over, or taking it back.
 *
 * Keyed on the PERSON and the year rather than on a row id, because the row may not exist yet —
 * see the header. `handedOut: true` stamps the time and the person doing it on the server; a
 * client-supplied timestamp is not evidence of anything.
 */
export const updateAccreditationRequestSchema = z
  .object({
    editionId: z.uuid(),
    /** `person:<uuid>` or `guest:<uuid>` — see `accreditationSubjectSchema`. */
    subject: accreditationSubjectSchema,
    /** A level set here beats every other. `null` gives the row back to its roles and shifts. */
    accreditationLevelId: z.uuid().nullish(),
    /**
     * «INGEN AKKREDITERING» (204). `true` is an explicit none that beats a shift and a role;
     * `null` in the field above is «take what the sources give», which is a different answer.
     * `false` alone lifts the none again. Refused together with a level.
     */
    levelCleared: z.boolean().optional(),
    /**
     * HVOR MANGE PASS SOM NÅ ER LEVERT UT PÅ DENNE RADEN — en absolutt verdi, ikke et pluss/minus.
     *
     * Atle, 2. september: «Jeg trenger funksjonalitet for å kun levere ut 1 om gangen.»
     *
     * ABSOLUTT OG IKKE EN DELTA, fordi to personer i den samme disken er den ordinære situasjonen:
     * to «+1» sendt samtidig fra to skjermer ville blitt to, og begge ville trodd de leverte det
     * andre passet. En absolutt verdi er den siste avsenderens svar, og skjermen viser hva raden
     * står på — som er hva den som står der leser fra listen foran seg.
     *
     * Serveren klipper mot `seats`: tre pass på en gjest med to plasser er en skrivefeil.
     */
    handedOutCount: z.number().int().nonnegative().max(MAX_SEATS_PER_ROW).optional(),
    /** Passtypen, og dagene den gjelder (#25a). Null nullstiller; undefined er «ikke nevnt». */
    accreditationPassId: z.uuid().nullish(),
    passDays: z.array(z.iso.date()).max(MAX_PASS_DAYS).optional(),
    roleLabel: z.string().trim().min(1).max(120).nullish(),
    company: z.string().trim().max(120).nullish(),
    note: z.string().trim().max(2000).nullish(),
    /** B1: personens kategori og rolle i årgangen, skrevet på deltakelsen. Ikke for en gjest. */
    category: participationCategorySchema.optional(),
    roleTitle: z.string().trim().max(120).nullish(),
  })
  .superRefine((value, ctx) => {
    if (value.levelCleared === true && typeof value.accreditationLevelId === 'string') {
      ctx.addIssue({
        code: 'custom',
        path: ['levelCleared'],
        message: 'A level and «no accreditation» are two answers to one question.',
      });
    }
  })
  .meta({ id: 'UpdateAccreditationRequest' });
export type UpdateAccreditationRequest = z.infer<typeof updateAccreditationRequestSchema>;

/** How many cards one hand-out call may cover. A queue at the desk, not a whole festival. */
export const MAX_BULK_ACCREDITATIONS = 200;

/** «Utskrift av kort skal kunne gjøres for et utvalg» — and handing out a stack is the same shape. */
export const bulkAccreditationRequestSchema = z
  .object({
    editionId: z.uuid(),
    subjects: z.array(accreditationSubjectSchema).min(1).max(MAX_BULK_ACCREDITATIONS),
    /*
     * «Hand out» på en BUNKE betyr alle plassene på hver rad, og det er riktig for det den er:
     * en kø ved disken hvor femti kort går over bordet på en gang. Én-om-gangen er den enkelte
     * radens knapp — se `handedOutCount` på update.
     */
    /**
     * `set_pass` KOM MED #25a, og den står ved siden av `set_level` og ikke inni den.
     *
     * Atle: «Passtypene settes opp i Innstillinger sammen med akkrediteringsnivåene, velges per
     * person OG I MASSEOPERASJON.» Per person fantes; bunken manglet — og bunken er hele grunnen
     * til at feltet finnes. Hundre frivillige får det samme dagspasset, og å åpne hver rad for å
     * si det er hundre ganger den samme setningen.
     *
     * TO HANDLINGER OG IKKE ÉN MED TO FELT: et nivå og et pass er to forskjellige avgjørelser om
     * den samme personen — hva de får LOV til, og hva de får UTLEVERT — og en handling som satte
     * begge ville tvunget den som bare mente det ene til å gjenta det andre.
     */
    action: z.enum(['hand_out', 'take_back', 'set_level', 'set_pass']),
    accreditationLevelId: z.uuid().nullish(),
    /** `set_level` with «Ingen akkreditering» (204): `true` and a null level. See update. */
    levelCleared: z.boolean().optional(),
    /** Passtypen `set_pass` setter. `null` tar den bort; `undefined` er en forglemmelse. */
    accreditationPassId: z.uuid().nullish(),
  })
  .superRefine((value, ctx) => {
    if (value.action === 'set_level' && value.accreditationLevelId === undefined) {
      ctx.addIssue({
        code: 'custom',
        path: ['accreditationLevelId'],
        message: 'A level is required, or null to clear it.',
      });
    }
    if (value.levelCleared === true && typeof value.accreditationLevelId === 'string') {
      ctx.addIssue({
        code: 'custom',
        path: ['levelCleared'],
        message: 'A level and «no accreditation» are two answers to one question.',
      });
    }
    /* Samme form som nivået over: `null` er en påstand, `undefined` er at feltet ble glemt. */
    if (value.action === 'set_pass' && value.accreditationPassId === undefined) {
      ctx.addIssue({
        code: 'custom',
        path: ['accreditationPassId'],
        message: 'A pass is required, or null to clear it.',
      });
    }
  })
  .meta({ id: 'BulkAccreditationRequest' });
export type BulkAccreditationRequest = z.infer<typeof bulkAccreditationRequestSchema>;
